import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CalendarClock, RefreshCw } from "lucide-react";
import type { ResearchReportSchedule } from "@roboscientist/contracts";
import { api } from "../api.js";
import { formatReportTimestamp } from "./reportPresentation.js";

export function ReportScheduleControls({
  rootThreadId,
  schedule,
  fallbackIntervalMinutes = 60,
  finalSnapshot = false,
  onChanged
}: {
  rootThreadId: string;
  schedule: ResearchReportSchedule | null;
  fallbackIntervalMinutes?: number;
  finalSnapshot?: boolean;
  onChanged?: () => void;
}) {
  const configuredInterval = schedule?.updateIntervalMinutes ?? fallbackIntervalMinutes;
  const [intervalInput, setIntervalInput] = useState(String(configuredInterval));
  const interval = Number(intervalInput);
  const intervalValid = /^\d+$/.test(intervalInput)
    && Number.isInteger(interval)
    && interval >= 1
    && interval <= 525_600;
  const finalAtRender = schedule ? schedule.status === "final" : finalSnapshot;

  useEffect(() => {
    setIntervalInput(String(configuredInterval));
  }, [configuredInterval]);

  const updateMutation = useMutation({
    mutationFn: () => api.updateResearchReportSchedule(rootThreadId, interval),
    onSuccess: () => onChanged?.()
  });
  const summarizeMutation = useMutation({
    mutationFn: () => api.summarizeResearchThread(rootThreadId, interval, finalAtRender),
    onSuccess: () => onChanged?.()
  });
  const effectiveSchedule = [
    schedule,
    updateMutation.data?.schedule,
    summarizeMutation.data?.schedule
  ].filter((candidate): candidate is ResearchReportSchedule => Boolean(candidate))
    .sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt))[0]
    ?? null;
  const busy = effectiveSchedule?.status === "running"
    || effectiveSchedule?.status === "publishing";
  const final = effectiveSchedule ? effectiveSchedule.status === "final" : finalSnapshot;
  const saveInterval = () => {
    if (!intervalValid || final || busy || interval === effectiveSchedule?.updateIntervalMinutes) return;
    updateMutation.mutate();
  };
  const error = summarizeMutation.error ?? updateMutation.error;
  const status = effectiveSchedule?.status === "running"
    ? "Ephemeral writer is summarizing the current evidence."
    : effectiveSchedule?.status === "publishing"
      ? "Writer completed; waiting for the immutable snapshot to be indexed."
      : effectiveSchedule?.status === "failed"
        ? effectiveSchedule.lastError ?? "The last report writer failed. You can retry now."
        : effectiveSchedule?.nextUpdateDueAt
          ? `Next automatic summary: ${formatReportTimestamp(effectiveSchedule.nextUpdateDueAt)}.`
          : final
            ? "This snapshot is final. Resume reporting to continue the same immutable series."
            : "No report has been published yet. Summarize now to create the first snapshot.";

  return <div className="report-schedule-controls">
    <div className="report-schedule-controls__actions">
      <label className="report-summary-interval">
        <CalendarClock size={13} />
        <input
          type="number"
          inputMode="numeric"
          min="1"
          max="525600"
          step="1"
          value={intervalInput}
          aria-label="Summary interval (minutes)"
          disabled={updateMutation.isPending || busy}
          onChange={(event) => setIntervalInput(event.target.value)}
          onBlur={saveInterval}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
        />
        <span>min</span>
      </label>
      <button
        className="report-summarize"
        type="button"
        onClick={() => summarizeMutation.mutate()}
        disabled={summarizeMutation.isPending || busy || !intervalValid}
        title={final
          ? "Resume this immutable report series and summarize new evidence"
          : !intervalValid
            ? "Enter a whole-number interval from 1 to 525,600 minutes"
            : effectiveSchedule?.latestReportId
              ? "Start an ephemeral writer for the next report snapshot"
              : "Start an ephemeral writer for the first report snapshot"}
      >
        <RefreshCw className={summarizeMutation.isPending || busy ? "is-spinning" : undefined} size={13} />
        {summarizeMutation.isPending
          ? "Requesting…"
          : busy
            ? effectiveSchedule?.status === "publishing" ? "Publishing…" : "Summarizing…"
            : final
              ? "Resume & summarize"
              : effectiveSchedule?.latestReportId ? "Summarize now" : "Create first report"}
      </button>
    </div>
    <div className={`report-summary-status${error || effectiveSchedule?.status === "failed" ? " report-summary-status--error" : ""}`} role={error ? "alert" : "status"}>
      {error?.message ?? status}
      {updateMutation.isPending ? " Saving cadence…" : null}
    </div>
  </div>;
}
